import { useEffect, useMemo } from 'react'

import {
  priceEngine,
  type PriceLineInput,
  type PriceLineResult,
  type RecalcTrigger,
} from '@/services/price-engine'
import { usePriceStore } from '@/stores/price-store'

/**
 * usePriceEngine — React bridge. All math stays inside PriceEngine.
 */
export function usePriceEngine() {
  const hydrate = usePriceStore((s) => s.hydrate)
  const ready = usePriceStore((s) => s.ready)
  const profiles = usePriceStore((s) => s.profiles)
  const activeProfileId = usePriceStore((s) => s.activeProfileId)
  const global = usePriceStore((s) => s.global)
  const categories = usePriceStore((s) => s.categories)
  const history = usePriceStore((s) => s.history)
  const logs = usePriceStore((s) => s.logs)
  const simulatorProfitPercent = usePriceStore((s) => s.simulatorProfitPercent)
  const simulatorDiscountPercent = usePriceStore((s) => s.simulatorDiscountPercent)
  const lastSummary = usePriceStore((s) => s.lastSummary)

  const setActiveProfile = usePriceStore((s) => s.setActiveProfile)
  const updateGlobal = usePriceStore((s) => s.updateGlobal)
  const updateProfile = usePriceStore((s) => s.updateProfile)
  const updateCategory = usePriceStore((s) => s.updateCategory)
  const setSimulatorProfit = usePriceStore((s) => s.setSimulatorProfit)
  const setSimulatorDiscount = usePriceStore((s) => s.setSimulatorDiscount)
  const setLastSummary = usePriceStore((s) => s.setLastSummary)
  const refreshAudit = usePriceStore((s) => s.refreshAudit)

  useEffect(() => {
    hydrate()
  }, [hydrate])

  const activeProfile = useMemo(
    () => profiles.find((p) => p.id === activeProfileId) ?? profiles[0] ?? null,
    [profiles, activeProfileId],
  )

  const calculateLine = (input: PriceLineInput, trigger: RecalcTrigger = 'components') =>
    priceEngine.calculateLine(input, trigger)

  const summarize = (
    lines: PriceLineResult[],
    discountPercent = 0,
    discountValue = 0,
    freight = 0,
  ) => {
    const summary = priceEngine.summarize(lines, discountPercent, discountValue, freight)
    setLastSummary(summary)
    return summary
  }

  const simulateLine = (purchasePrice: number, quantity = 1, category?: string | null) =>
    priceEngine.simulateProfitPercent(
      purchasePrice,
      quantity,
      simulatorProfitPercent,
      category,
    )

  const health = lastSummary ? priceEngine.getHealth(lastSummary) : 'healthy'
  const alerts = lastSummary ? priceEngine.getAlerts(lastSummary) : []

  return {
    ready,
    profiles,
    activeProfile,
    activeProfileId,
    global,
    categories,
    history,
    logs,
    simulatorProfitPercent,
    simulatorDiscountPercent,
    lastSummary,
    health,
    alerts,
    setActiveProfile,
    updateGlobal,
    updateProfile,
    updateCategory,
    setSimulatorProfit,
    setSimulatorDiscount,
    setLastSummary,
    calculateLine,
    summarize,
    simulateLine,
    refreshAudit,
    engine: priceEngine,
  }
}
